/**
 * The one place that turns a trip's money fields into a number.
 *
 * Pure functions only — no DOM, no Firebase.
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 * On 14 Aug the Account screen reported $100 across nine trips and `/insights`
 * reported $1,000,020,000 across the same nine, on the same day. Two screens,
 * two hand-rolled parsers, two answers. The legacy app has at least three
 * copies of this arithmetic (`getTripValue`, `getPipelineStage`, and
 * `money()` in `insights.html`), and they do not agree about what a string
 * containing more than one number means.
 *
 * HANDOFF.md §9 asks for exactly this: a shared money parser used by both
 * screens, because "the two disagreeing at all is the deeper problem". This
 * is that parser. Account (`features/account/`) uses it today. `/insights`
 * and step 3.4 adopt it when they are ported — do not write a fourth copy.
 *
 * ── The bug this fixes on the way past ────────────────────────────────────
 * Legacy: `parseFloat(String(v || '0').replace(/[^0-9.-]/g, ''))`.
 *
 * A field holding "TBD" strips to "" and `parseFloat('')` is NaN. The legacy
 * loop adds that straight into a running total, so ONE non-numeric amount
 * among twenty turns an advisor's whole revenue figure into "$NaN". Nothing
 * guarded it. `amount()` returns 0 for anything it cannot read, which is the
 * only safe direction: an under-count is visible and wrong, NaN is a screen
 * that stops working.
 *
 * ── CENTS: the deliberate decision, 15 Aug (step 3.4) ─────────────────────
 * `amount()` returns DOLLARS, because that is what every caller and every
 * display helper already expects. But every SUM and every SPLIT in this file
 * and its siblings (`commission.js`, `revenue.js`) is computed in INTEGER
 * CENTS and converted back once, at the end.
 *
 * Measured, not assumed:
 *
 *     0.1 + 0.2                          === 0.30000000000000004
 *     ten line items of 1234.56 summed   === 12345.599999999997
 *     333.33 * 0.7 + 333.33 * 0.3        === 333.3299999999999
 *
 * The last one is the one that costs money: an advisor's 70% share plus the
 * agency's 30% share not adding up to the commission. In cents the same three
 * are exact. The cost of the choice is a ceiling — `Number.MAX_SAFE_INTEGER`
 * cents is $90,071,992,547,409.91 — and values past it are CLAMPED rather
 * than allowed to drift into nonsense. A trip priced above ninety trillion
 * dollars is corrupt data, and a clamped number is visibly absurd where a
 * silently-wrong one is not.
 *
 * Every exported function returns a `Number.isFinite` value. NaN is not a
 * possible output of anything here; that failure mode already shipped once.
 */

/** How many `fin{n}amt` fields a trip can hold. Legacy constant. */
export const FIN_SLOTS = 20

/** How many payment rows a trip can hold. Legacy loops 10; the page renders 3. */
export const PAY_SLOTS = 10

/** The largest number of cents that survives integer arithmetic exactly. */
export const CENTS_LIMIT = Number.MAX_SAFE_INTEGER

/**
 * Read one money field. Never NaN, never Infinity.
 *
 * Deliberately conservative about strings holding more than one number:
 * "1,200" is one number and reads as 1200, but "1000 or 2000" is ambiguous
 * and reads as 1000 (the first), NOT 10002000 (the digits concatenated).
 * Concatenation is what produced the $1,000,020,000 shape in `/insights` —
 * stripping every separator out of a multi-number string manufactures an
 * enormous value out of two small ones.
 *
 * DO NOT CHANGE THIS FUNCTION without changing the port in `insights.html`'s
 * MONEY:START/END block in the same commit. `tests/legacy/insights-money.check.mjs`
 * compares the two on 21 cases and fails if they drift.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function amount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0

  const raw = String(value == null ? '' : value).trim()
  if (!raw) return 0

  /* Take the FIRST number in the string and stop. Thousands separators are
     part of a number; a second number is a separate number. */
  const cleaned = raw.replace(/(\d),(?=\d{3}\b)/g, '$1')
  const match = cleaned.match(/\d+(?:\.\d+)?/)
  if (!match) return 0

  const parsed = Number.parseFloat(match[0])
  if (!Number.isFinite(parsed)) return 0

  /* The minus sign is not always next to the digits. "-$1,500.25" puts a
     currency symbol between them, and a regex anchored on `-?\d` reads that
     as +1500.25 — a refund silently becoming income. Negative when a minus
     is the last thing before the number that is not part of it. */
  const negative = /-[^0-9]*$/.test(cleaned.slice(0, match.index))
  return negative ? -parsed : parsed
}

/**
 * Round away from zero, so that a refund is the exact mirror of the charge.
 *
 * `Math.round(-0.5)` is `-0` and `Math.round(0.5)` is `1`; splitting a credit
 * of -$1.00 would not mirror splitting a charge of $1.00. Half a cent is not
 * money anyone loses sleep over, but an asymmetry in the sign of a rounding
 * rule is the kind of thing that makes two reports disagree by a penny
 * forever and nobody able to say which is right.
 *
 * @param {number} n
 * @returns {number}
 */
function roundHalfAwayFromZero(n) {
  return n < 0 ? -Math.round(-n) : Math.round(n)
}

/**
 * Keep a cent count inside the exactly-representable range. NaN becomes 0.
 * @param {number} cents
 * @returns {number}
 */
export function clampCents(cents) {
  const n = Number(cents)
  if (Number.isNaN(n)) return 0
  if (n >= CENTS_LIMIT) return CENTS_LIMIT
  if (n <= -CENTS_LIMIT) return -CENTS_LIMIT
  return roundHalfAwayFromZero(n)
}

/**
 * A money field as whole cents. The unit all arithmetic here happens in.
 * @param {unknown} value
 * @returns {number}
 */
export function toCents(value) {
  const dollars = amount(value)
  if (dollars === 0) return 0
  const scaled = dollars < 0 ? -(-dollars * 100) : dollars * 100
  return clampCents(scaled)
}

/**
 * Cents back to dollars, for display and for every value this module returns.
 * @param {number} cents
 * @returns {number}
 */
export function fromCents(cents) {
  return clampCents(cents) / 100
}

/**
 * Add up money without floating-point drift. Accepts anything `amount()` reads.
 * @param {unknown[]|null|undefined} values
 * @returns {number}
 */
export function sumMoney(values) {
  const list = Array.isArray(values) ? values : []
  let cents = 0
  for (const value of list) cents = clampCents(cents + toCents(value))
  return fromCents(cents)
}

/**
 * Sum an indexed family of fields — `fin0amt`…`fin19amt`.
 *
 * @param {Record<string, any>|null|undefined} fields
 * @param {string} prefix - 'fin'
 * @param {number} slots
 * @returns {number}
 */
export function sumSlots(fields, prefix, slots) {
  const data = fields || {}
  const count = Number.isFinite(slots) ? Math.max(0, Math.floor(Number(slots))) : 0
  let cents = 0
  for (let i = 0; i < count; i++) cents = clampCents(cents + toCents(data[`${prefix}${i}amt`]))
  return fromCents(cents)
}

/**
 * Is this a SUMMARY rather than a whole trip?
 *
 * `trip-summary.js` stamps `summaryVersion` on everything it produces and no
 * trip document has ever carried that key, so it is an unambiguous marker.
 *
 * It matters because every money total here is derived from `trip.data` —
 * line-item slots, payment rows, commission rows — and a summary has no
 * `data`. Fed one, these functions returned ZERO rather than failing: a
 * dashboard drawn from the summary index showed £0 revenue with nothing in
 * the console. The precomputed totals the summary carries are the same
 * numbers, produced by these same functions when the index was built.
 *
 * @param {any} trip @returns {boolean}
 */
export function isSummary(trip) {
  return !!trip && trip.summaryVersion !== undefined
}

/** A precomputed total from a summary, or null when this is a whole trip. */
export function summaryNumber(trip, key) {
  if (!isSummary(trip)) return null
  const value = Number(trip[key])
  return Number.isFinite(value) ? value : 0
}

/**
 * What a trip is worth: the sum of its line items.
 *
 * Matches legacy `getTripValue` (app.html:7219) field for field, minus the
 * NaN hole. Trip money lives in `trip.data`, not on the trip document itself
 * — the `t.pages[].fields` shape some older code assumed never existed.
 *
 * NOTE: this deliberately ignores `data.finTotal`, the quoted total. Legacy's
 * trip dashboard falls back to it when the line items are empty (app.html:7051)
 * and `/insights` reads ONLY it. Which of the two a screen should show is an
 * open product question (HANDOFF §9) — `revenue.js#tripRevenue` names the
 * three answers instead of quietly picking one here.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function tripValue(trip) {
  const pre = summaryNumber(trip, 'revenue')
  if (pre !== null) return pre
  return sumSlots(trip && trip.data, 'fin', FIN_SLOTS)
}

/**
 * One payment row, read under BOTH names legacy uses for it.
 *
 * ── BUG: `pay{n}amt` does not exist ───────────────────────────────────────
 * The Financial Summary page writes `pay0_amt`, `pay1_amt`, `pay2_amt`
 * (app.html:5163, the only place payment rows are created). `getPipelineStage`
 * (app.html:7208) and `getTripPaidAmount` (app.html:7229) read `pay0amt` —
 * no underscore. That name is never written by anything, so both legacy
 * functions have always returned 0 for every trip that has ever existed, and
 * the pipeline board could not reach "deposited" or "paid" by any amount of
 * money changing hands. This file inherited the misspelling on 15 Aug.
 *
 * `pay{n}_amt` wins; `pay{n}amt` is still read so that no hand-written or
 * test fixture holding the old name silently becomes worth nothing.
 *
 * @param {Record<string, any>} data
 * @param {number} i
 * @returns {unknown}
 */
function payAmountField(data, i) {
  const underscored = data[`pay${i}_amt`]
  return underscored == null ? data[`pay${i}amt`] : underscored
}

/**
 * The payment rows a trip carries, in slot order.
 *
 * `status` is the advisor's `Paid` / `Unpaid` dropdown. A row is only money
 * the advisor HAS when it says `Paid` — see `tripPaid`.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {number} [slots]
 * @returns {Array<{slot: number, label: string, amount: number, due: string, status: string, method: string, paid: boolean}>}
 */
export function paymentRows(trip, slots = PAY_SLOTS) {
  const data = (trip && trip.data) || {}
  const count = Number.isFinite(slots) ? Math.max(0, Math.floor(Number(slots))) : 0
  const labels = ['Deposit', '2nd Payment', 'Final']
  const rows = []
  for (let i = 0; i < count; i++) {
    const raw = payAmountField(data, i)
    const due = String(data[`pay${i}_due`] ?? '')
    const status = String(data[`pay${i}_paid`] ?? '')
    const value = amount(raw)
    if (value === 0 && !due && !status) continue
    rows.push({
      slot: i,
      label: labels[i] || `Payment ${i + 1}`,
      amount: value,
      due,
      status,
      method: String(data[`pay${i}_method`] ?? ''),
      paid: status.trim().toLowerCase() === 'paid',
    })
  }
  return rows
}

/**
 * Money the advisor has actually COLLECTED — rows marked `Paid` and no others.
 *
 * ── BUG: legacy counted the schedule, not the payments ────────────────────
 * `getTripPaidAmount` (app.html:7229) sums every payment row regardless of
 * its status dropdown. Those rows are the payment PLAN, filled in when the
 * quote is written, so the moment the field-name bug above is fixed a trip
 * whose deposit is merely scheduled would read as "deposited" and a fully
 * scheduled trip as "paid" — booked revenue nobody has received. Legacy's
 * own trip dashboard gets this right (app.html:7070, `if (p.status ===
 * 'Paid')`) and is the behaviour ported here. `tripScheduled` is the other
 * number, named so the two can never be confused again.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function tripPaid(trip) {
  const pre = summaryNumber(trip, 'collected')
  if (pre !== null) return pre
  let cents = 0
  for (const row of paymentRows(trip)) {
    if (row.paid) cents = clampCents(cents + toCents(row.amount))
  }
  return fromCents(cents)
}

/**
 * Every payment row, paid or not: what the client has agreed to pay in total.
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function tripScheduled(trip) {
  const pre = summaryNumber(trip, 'scheduled')
  if (pre !== null) return pre
  let cents = 0
  for (const row of paymentRows(trip)) cents = clampCents(cents + toCents(row.amount))
  return fromCents(cents)
}

/**
 * Scheduled but not yet collected.
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function tripOutstanding(trip) {
  return fromCents(toCents(tripScheduled(trip)) - toCents(tripPaid(trip)))
}

/**
 * Is a trip cancelled? Cancelled trips count for nothing anywhere.
 * @param {Record<string, any>|null|undefined} trip
 * @returns {boolean}
 */
export function isCancelled(trip) {
  return String((trip && trip.status) || '').toLowerCase() === 'cancelled'
}

/**
 * Where a trip sits in the pipeline. Ported from legacy `getPipelineStage`
 * (app.html:7191) so that "booked" means the same thing on the Account screen
 * as it does on the pipeline board — they disagreed before only because each
 * counted the money itself.
 *
 * @param {any} trip
 * @param {string[]} [knownStages] - the ids `PIPELINE_STAGES` allows
 * @returns {'new'|'proposal'|'deposited'|'paid'|'completed'|string}
 */
export function pipelineStage(trip, knownStages) {
  const t = trip || {}
  const stages = knownStages || ['new', 'proposal', 'deposited', 'paid', 'completed']
  if (t.pipelineStage && stages.includes(t.pipelineStage)) return t.pipelineStage

  const status = String(t.status || '').toLowerCase()
  if (status === 'completed' || status === 'archived') return 'completed'

  const cost = tripValue(t)
  const paid = tripPaid(t)
  if (cost > 0 && paid >= cost) return 'paid'
  if (paid > 0) return 'deposited'
  if (cost > 0) return 'proposal'
  return 'new'
}

/**
 * Has this trip been booked, in the sense the Account cards mean?
 *
 * An explicit status wins. Otherwise it is the pipeline's view: money has
 * moved. Cancelled is never booked, whatever else it says.
 *
 * @param {any} trip
 * @returns {boolean}
 */
export function isBooked(trip) {
  if (!trip || isCancelled(trip)) return false
  const status = String(trip.status || '').toLowerCase()
  if (status === 'booked' || status === 'confirmed' || status === 'completed') return true
  const stage = pipelineStage(trip)
  return stage === 'deposited' || stage === 'paid' || stage === 'completed'
}
